/**
 * Holiday calendar: month grid showing public holidays and employee absences (approved leave).
 * Weekends styled like Team calendar; click a day with absences to see who is off and why.
 */
import React, { useState, useEffect, useMemo } from 'react';
import hrApi from '../api';
import { formatDisplayName } from '../utils/formatTime';
import './HolidayCalendar.css';

function toDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDaysInMonth(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days = [];
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d));
  }
  const startWeekday = (first.getDay() + 6) % 7;
  return { days, startWeekday };
}

function HolidayCalendar({ refreshKey = 0 }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const firstDayOfMonth = useMemo(() => `${year}-${String(month).padStart(2, '0')}-01`, [year, month]);
  const lastDayOfMonth = useMemo(() => {
    const d = new Date(year, month, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [year, month]);

  const [absencesByDate, setAbsencesByDate] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    hrApi
      .get('/api/holidays/calendar-view', { params: { startDate: firstDayOfMonth, endDate: lastDayOfMonth } })
      .then((r) => {
        if (!cancelled && r.data) {
          setPublicHolidays(Array.isArray(r.data.publicHolidays) ? r.data.publicHolidays : []);
          setAbsencesByDate(typeof r.data.absencesByDate === 'object' && r.data.absencesByDate ? r.data.absencesByDate : {});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublicHolidays([]);
          setAbsencesByDate({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [firstDayOfMonth, lastDayOfMonth, refreshKey]);

  const publicHolidaysByDate = useMemo(() => {
    const map = {};
    publicHolidays.forEach((h) => {
      if (!h || !h.date) return;
      const start = new Date(h.date);
      start.setHours(0, 0, 0, 0);
      const end = h.endDate ? new Date(h.endDate) : new Date(h.date);
      end.setHours(0, 0, 0, 0);
      const entry = { name: h.name || 'Public holiday', nameFr: h.nameFr };
      const d = new Date(start);
      while (d <= end) {
        const key = toDateKey(d);
        if (!map[key]) map[key] = [];
        map[key].push(entry);
        d.setDate(d.getDate() + 1);
      }
    });
    return map;
  }, [publicHolidays]);

  const { days, startWeekday } = useMemo(() => getDaysInMonth(year, month - 1), [year, month]);
  const monthName = useMemo(() => new Date(year, month - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' }), [year, month]);

  const monthSummary = useMemo(() => {
    // Match the calendar badges only: Mon–Fri cells with absences, not public holidays.
    // (Backend may still list leave on Sat/Sun for shift workers; those days are not shown as absence tiles here, so we do not count them in this total.)
    const absenceInstances = days.reduce((sum, day) => {
      const key = toDateKey(day);
      const dow = day.getDay();
      const isMonFri = dow >= 1 && dow <= 5;
      if (!isMonFri) return sum;
      if ((publicHolidaysByDate[key] || []).length > 0) return sum;
      const arr = absencesByDate[key] || [];
      return sum + (Array.isArray(arr) ? arr.length : 0);
    }, 0);
    return { absenceInstances };
  }, [days, publicHolidaysByDate, absencesByDate]);

  const handlePrevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); } else { setMonth((m) => m - 1); }
  };
  const handleNextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); } else { setMonth((m) => m + 1); }
  };

  return (
    <div className="holiday-calendar">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap w-100">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handlePrevMonth} aria-label="Previous month">
            <i className="bi bi-chevron-left" />
          </button>
          <span className="hc-month-title">{monthName}</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleNextMonth} aria-label="Next month">
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="hc-loading">Loading...</div>
      ) : (
        <div className="hc-grid">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((wd) => (
            <div key={wd} className="hc-cell hc-header">{wd}</div>
          ))}
          {Array.from({ length: startWeekday }, (_, i) => (
            <div key={`empty-${i}`} className="hc-cell hc-empty" />
          ))}
          {days.map((day) => {
            const key = toDateKey(day);
            const todayKey = toDateKey(now);
            const isToday = key === todayKey;
            const dayOfWeek = day.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            const publicOnDay = publicHolidaysByDate[key] || [];
            const absencesOnDay = absencesByDate[key] || [];
            const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
            const hasAbsences = isWeekday && publicOnDay.length === 0 && absencesOnDay.length > 0;

            return (
              <div
                key={key}
                className={`hc-cell hc-day ${isToday ? 'hc-today' : ''} ${isWeekend ? 'hc-weekend' : ''} ${publicOnDay.length ? 'hc-publicholiday' : ''} ${hasAbsences ? 'hc-has-absence' : ''}`}
              >
                <div className="hc-day-num">{day.getDate()}</div>
                {isWeekend && !publicOnDay.length && !hasAbsences && (
                  <span className="hc-day-label">Weekend</span>
                )}
                {!isWeekend && !publicOnDay.length && !hasAbsences && (
                  <span className="hc-day-label hc-day-label-working">All present</span>
                )}
                {publicOnDay.length > 0 && (
                  <div className="hc-day-public">
                    {publicOnDay.map((p, i) => (
                      <span key={i} className="badge bg-secondary hc-badge-ph">{p.name}</span>
                    ))}
                  </div>
                )}
                {hasAbsences && (
                  <button
                    type="button"
                    className="hc-day-absence-btn"
                    onClick={() => setSelectedDay({ dateKey: key, dateLabel: day.toLocaleDateString(undefined, { dateStyle: 'medium' }), absences: absencesOnDay })}
                    title="Click to see who is absent"
                  >
                    <span className="badge bg-info text-dark">
                      {absencesOnDay.length} absent{absencesOnDay.length !== 1 ? 's' : ''}
                      <i className="bi bi-chevron-right ms-1 small" aria-hidden />
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && (
        <div className="hc-footer mt-4">
          <div className="hc-legend d-flex flex-wrap align-items-center gap-3 gap-md-4 mb-2">
            <span className="d-flex align-items-center gap-2">
              <span className="hc-legend-swatch hc-legend-weekend" aria-hidden /> Weekend
            </span>
            <span className="d-flex align-items-center gap-2">
              <span className="hc-legend-swatch hc-legend-ph" aria-hidden /> Public holiday
            </span>
            <span className="d-flex align-items-center gap-2">
              <span className="hc-legend-swatch hc-legend-absence" aria-hidden /> Absences (click to see who)
            </span>
          </div>
          <p className="small text-muted mb-0">
            {monthSummary.absenceInstances > 0
              ? `${monthSummary.absenceInstances} approved absence${monthSummary.absenceInstances !== 1 ? 's' : ''} this month.`
              : 'No approved leave this month.'}
            {monthSummary.absenceInstances === 0 && ' Add public holidays via "Manage public holidays" above.'}
          </p>
        </div>
      )}

      {selectedDay && selectedDay.absences && selectedDay.absences.length > 0 && (
        <div className="hc-modal-backdrop" onClick={() => setSelectedDay(null)} role="presentation">
          <div className="hc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hc-modal-header">
              <h3>Absent on {selectedDay.dateLabel}</h3>
              <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedDay(null)} />
            </div>
            <div className="hc-modal-body">
              <p className="small text-muted mb-3">Employees on leave on this day.</p>
              <ul className="list-unstyled mb-0">
                {selectedDay.absences.map((a, i) => (
                  <li key={i} className="hc-absence-item">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <strong>{formatDisplayName(a.username)}</strong>
                      {(a.halfDay === 'am' || a.halfDay === 'pm') && (
                        <span className="badge bg-warning text-dark">
                          {a.halfDay === 'am' ? 'Morning off (0.5d)' : 'Afternoon off (0.5d)'}
                        </span>
                      )}
                    </div>
                    <div className="small mt-1">
                      <span className="text-muted">From {a.startDate} to {a.endDate}</span>
                    </div>
                    {a.comment ? (
                      <div className="small text-muted mt-0">Reason: {a.comment}</div>
                    ) : (
                      <div className="small text-muted mt-0">On leave</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HolidayCalendar;
